import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { call } from '../api';
import { cycleRepeat, fmtTime, jumpTo, next, prev, seek, setFull, setVolume, storable, toggle, toggleShuffle, useFullPlayer, useMusic, type MusicTrack } from '../musicPlayer';
import { savePrefs, usePrefs, type Prefs } from '../prefs';

/**
 * Volledige muziekspeler (over het hele scherm), in vijf weergaven: Hoes, Klassiek, Vinyl, Songtekst
 * en Kleurverloop. Breed scherm: beeld links, songtekst in het midden, wachtrij rechts. Smal: alles
 * onder elkaar, songtekst en wachtrij als paneel. Omlaag vegen, ⌄ of Esc = terug naar de mini-speler.
 */

type View = Prefs['playerView'];
export const PLAYER_VIEWS: [View, string][] = [['cover', 'Hoes'], ['classic', 'Klassiek'], ['vinyl', 'Vinyl'], ['lyrics', 'Songtekst'], ['gradient', 'Kleurverloop']];

interface LyricLine { t: number; text: string }
interface Lyrics { synced: LyricLine[] | null; plain: string | null; instrumental: boolean }

const lyricsCache = new Map<string, Promise<Lyrics | null>>();
function useLyrics(t: MusicTrack | undefined, on: boolean): Lyrics | null | undefined {
  const [l, setL] = useState<Lyrics | null | undefined>(undefined);
  const key = t ? `${t.artist}|${t.title}` : '';
  useEffect(() => {
    setL(undefined);
    if (!t || !on) return;
    let p = lyricsCache.get(key);
    if (!p) {
      const q = new URLSearchParams({ artist: t.artist, title: t.title, album: t.album ?? '', duration: String(Math.round(t.duration ?? 0)) });
      p = call<{ lyrics: Lyrics | null }>(`/api/music/lyrics?${q}`).then((r) => r.lyrics).catch(() => null);
      lyricsCache.set(key, p);
    }
    let live = true;
    void p.then((x) => live && setL(x));
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, on]);
  return l;
}

/** Gemiddelde kleur van de hoes (lukt alleen als de afbeelding dat toestaat; anders null). */
const colorCache = new Map<string, string | null>();
function useArtColor(url: string | null | undefined): string | null {
  const [c, setC] = useState<string | null>(url ? colorCache.get(url) ?? null : null);
  useEffect(() => {
    if (!url) return setC(null);
    if (colorCache.has(url)) return setC(colorCache.get(url) ?? null);
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const cv = document.createElement('canvas');
        cv.width = 24;
        cv.height = 24;
        const ctx = cv.getContext('2d')!;
        ctx.drawImage(img, 0, 0, 24, 24);
        const d = ctx.getImageData(0, 0, 24, 24).data;
        let r = 0, g = 0, b = 0, n = 0;
        for (let i = 0; i < d.length; i += 4) {
          const mx = Math.max(d[i], d[i + 1], d[i + 2]);
          const mn = Math.min(d[i], d[i + 1], d[i + 2]);
          const w = 1 + (mx - mn) / 32; // kleurrijke pixels tellen zwaarder
          r += d[i] * w; g += d[i + 1] * w; b += d[i + 2] * w; n += w;
        }
        const col = `rgb(${Math.round(r / n)}, ${Math.round(g / n)}, ${Math.round(b / n)})`;
        colorCache.set(url, col);
        setC(col);
      } catch {
        colorCache.set(url, null);
        setC(null);
      }
    };
    img.onerror = () => {
      colorCache.set(url, null);
      setC(null);
    };
    img.src = url;
  }, [url]);
  return c;
}

function LyricsView({ lyrics, time, big = false }: { lyrics: Lyrics | null | undefined; time: number; big?: boolean }) {
  const box = useRef<HTMLDivElement>(null);
  const synced = lyrics?.synced;
  let cur = -1;
  if (synced) for (let i = 0; i < synced.length; i++) if (synced[i].t <= time + 0.25) cur = i;
  useEffect(() => {
    const el = box.current?.querySelector<HTMLElement>('[data-cur="true"]');
    if (el && box.current) box.current.scrollTo({ top: el.offsetTop - box.current.clientHeight * 0.35, behavior: 'smooth' });
  }, [cur]);
  if (lyrics === undefined) return <div className="fp-lyrics fp-lyrics-empty">Songtekst zoeken…</div>;
  if (!lyrics || (!synced && !lyrics.plain)) return <div className="fp-lyrics fp-lyrics-empty">{lyrics?.instrumental ? 'Instrumentaal ♪' : 'Geen songtekst gevonden.'}</div>;
  return (
    <div className={`fp-lyrics${big ? ' is-big' : ''}`} ref={box}>
      {synced ? synced.map((l, i) => (
        <button key={i} type="button" className={`fp-line${i === cur ? ' is-cur' : i < cur ? ' is-past' : ''}`} data-cur={i === cur} onClick={() => seek(l.t)}>
          {l.text || '♪'}
        </button>
      )) : <p className="fp-plain">{lyrics.plain}</p>}
      <span className="fp-credit">Songtekst via LRCLIB</span>
    </div>
  );
}

export function FullPlayer() {
  const open = useFullPlayer();
  const s = useMusic();
  const prefs = usePrefs();
  const [panel, setPanel] = useState<'lyrics' | 'queue' | null>(null);
  const panelRef = useRef(panel);
  panelRef.current = panel;
  const [settings, setSettings] = useState(false);
  const [fav, setFav] = useState<Record<string, boolean>>({});
  const touch = useRef<number | null>(null);
  const t = s.queue[s.index];
  const view = prefs.playerView;
  const lyrics = useLyrics(t, open && prefs.playerLyrics);
  const artColor = useArtColor(open && prefs.playerAccent === 'auto' ? t?.artwork : null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' || e.key === 'Esc' || e.keyCode === 27 || e.key === 'GoBack' || e.key === 'BrowserBack') {
        e.preventDefault();
        e.stopImmediatePropagation();
        // Eerst een open lade (songtekst/wachtrij) dicht, daarna de speler
        if (panelRef.current) setPanel(null);
        else setFull(false);
      } else if (e.key === ' ' && !(e.target instanceof HTMLInputElement) && !(e.target instanceof HTMLButtonElement)) {
        e.preventDefault();
        toggle();
      }
    };
    window.addEventListener('keydown', onKey, true);
    document.body.classList.add('fp-open');
    return () => {
      window.removeEventListener('keydown', onKey, true);
      document.body.classList.remove('fp-open');
    };
  }, [open]);

  if (!open || !t) return null;
  const dur = s.duration || t.duration || 0;
  const favKey = `${t.source}:${t.addonId ?? ''}:${t.id}`;
  const accent = prefs.playerAccent !== 'auto' ? prefs.playerAccent : artColor ? lighten(artColor) : '#ffffff';
  const style = { '--fp-tint': artColor ?? '#3a3f4b', '--fp-accent': accent, '--fp-art': t.artwork ? `url("${t.artwork}")` : 'none' } as CSSProperties;
  const source = t.addonName ? `via ${t.addonName}` : t.source === 'plex' ? 'via Plex' : '';
  const save = (p: Partial<Prefs>) => void savePrefs(p);
  const showLyrics = prefs.playerLyrics && (view === 'lyrics' || panel === 'lyrics');
  const upNext = s.queue[s.index + 1];

  const toggleFav = async () => {
    const on = !fav[favKey];
    setFav((f) => ({ ...f, [favKey]: on }));
    await call('/api/music/favorites', { method: 'PUT', body: JSON.stringify({ track: storable(t), on }) }).catch(() => setFav((f) => ({ ...f, [favKey]: !on })));
  };

  const visual = view === 'vinyl' ? (
    <div className={`fp-vinyl${s.playing && prefs.playerSpin ? ' is-spinning' : ''}`} aria-hidden="true">
      <div className="fp-deck">
        <div className="fp-disc"><span className="fp-label" /><span className="fp-hole" /></div>
        <span className={`fp-arm${s.playing ? ' is-on' : ''}`} />
      </div>
    </div>
  ) : view === 'lyrics' ? (
    <div className="fp-mini-head"><span className="fp-art fp-art-sm" /><span><strong>{t.title}</strong><small>{t.artist}</small></span></div>
  ) : (
    <div className="fp-art-wrap"><span className="fp-art" role="img" aria-label={`Hoes van ${t.album ?? t.title}`} /></div>
  );

  return (
    <div
      className={`fp fp-v-${view}${prefs.playerBlur ? ' fp-blur' : ''}${artColor ? '' : ' fp-notint'}${panel ? ' has-panel' : ''}`}
      style={style}
      role="dialog"
      aria-modal="true"
      aria-label={`Nu speelt: ${t.title} van ${t.artist}`}
      onTouchStart={(e) => { touch.current = e.touches[0].clientY; }}
      onTouchEnd={(e) => {
        if (touch.current !== null && e.changedTouches[0].clientY - touch.current > 140 && !(e.target as HTMLElement).closest('.fp-lyrics, .fp-queue, input')) setFull(false);
        touch.current = null;
      }}
    >
      <div className="fp-bg" aria-hidden="true" />
      <header className="fp-top" data-focus-group>
        <button type="button" className="fp-icon" onClick={() => setFull(false)} aria-label="Verkleinen naar mini-speler">⌄</button>
        <span className="fp-from">{t.album ? <>SPEELT VAN <b>{t.album}</b></> : 'NU SPEELT'}</span>
        <nav className="fp-views" aria-label="Weergave">
          {PLAYER_VIEWS.map(([id, label]) => (
            <button key={id} type="button" className="fp-chip" aria-pressed={view === id} onClick={() => save({ playerView: id })}>{label}</button>
          ))}
        </nav>
        <button type="button" className="fp-icon" aria-pressed={settings} onClick={() => setSettings((v) => !v)} aria-label="Speler aanpassen">⚙</button>
      </header>

      <div className="fp-body">
        <section className="fp-main">
          {visual}
          {view === 'cover' && <div className="fp-cover-title" aria-hidden="true">{t.album ?? t.title}</div>}
          {view === 'lyrics' && <LyricsView lyrics={lyrics} time={s.time} big />}
          <div className="fp-meta">
            <div className="fp-titles">
              <strong>{t.title}</strong>
              <span>{t.artist}{source ? ` · ${source}` : ''}</span>
            </div>
          </div>
          <div className="fp-actions" data-focus-group>
            <button type="button" className={`fp-icon${fav[favKey] ? ' is-on' : ''}`} onClick={() => void toggleFav()} aria-label={fav[favKey] ? 'Uit favorieten' : 'Aan favorieten'}>{fav[favKey] ? '♥' : '♡'}</button>
            <span className="fp-vol"><span aria-hidden="true">🔈</span><input className="fp-volume" type="range" min={0} max={1} step={0.02} value={s.volume} onChange={(e) => setVolume(Number(e.target.value))} aria-label="Volume" style={{ '--p': `${s.volume * 100}%` } as CSSProperties} /></span>
            {prefs.playerLyrics && view !== 'lyrics' && <button type="button" className={`fp-icon${panel === 'lyrics' ? ' is-on' : ''}`} onClick={() => setPanel(panel === 'lyrics' ? null : 'lyrics')} aria-label="Songtekst" aria-pressed={panel === 'lyrics'}>❝</button>}
            <button type="button" className={`fp-icon${panel === 'queue' ? ' is-on' : ''}`} onClick={() => setPanel(panel === 'queue' ? null : 'queue')} aria-label="Wachtrij" aria-pressed={panel === 'queue'}>☰</button>
          </div>
          {upNext && <div className="fp-upnext"><span>HIERNA</span> {upNext.title} • {upNext.artist}</div>}
          <div className="fp-progress">
            <input type="range" min={0} max={Math.max(1, dur)} step={1} value={Math.min(s.time, dur)} onChange={(e) => seek(Number(e.target.value))} aria-label="Positie in nummer" style={{ '--p': `${dur ? (Math.min(s.time, dur) / dur) * 100 : 0}%` } as CSSProperties} />
            <div className="fp-times">
              <span>{fmtTime(s.time)}</span>
              {prefs.playerQuality && s.quality && <span className="fp-quality">{s.quality}</span>}
              <span>-{fmtTime(Math.max(0, dur - s.time))}</span>
            </div>
            {s.error && <p className="fp-error" role="alert">{s.error}</p>}
          </div>
          <div className="fp-controls" data-focus-group>
            <button type="button" className={`fp-icon${s.shuffle ? ' is-on' : ''}`} onClick={toggleShuffle} aria-pressed={s.shuffle} aria-label="Shuffle">⤮</button>
            <button type="button" className="fp-big" onClick={prev} aria-label="Vorige">⏮</button>
            <button type="button" className="fp-play" onClick={toggle} aria-label={s.playing ? 'Pauze' : 'Afspelen'} data-autofocus>{s.loading ? '…' : s.playing ? '❚❚' : '▶'}</button>
            <button type="button" className="fp-big" onClick={() => next()} aria-label="Volgende">⏭</button>
            <button type="button" className={`fp-icon${s.repeat !== 'off' ? ' is-on' : ''}`} onClick={cycleRepeat} aria-label={`Herhalen: ${s.repeat}`}>{s.repeat === 'one' ? '↻1' : '↻'}</button>
          </div>
        </section>

        {showLyrics && view !== 'lyrics' && (
          <aside className="fp-side fp-side-lyrics" aria-label="Songtekst">
            <div className="fp-side-head"><b>Songtekst</b><button type="button" className="fp-x" onClick={() => setPanel(null)} aria-label="Songtekst sluiten">✕</button></div>
            <LyricsView lyrics={lyrics} time={s.time} />
          </aside>
        )}
        {panel === 'queue' && (
          <aside className="fp-side fp-queue" aria-label="Wachtrij">
            <div className="fp-side-head"><b>Wachtrij</b><button type="button" className="fp-x" onClick={() => setPanel(null)} aria-label="Wachtrij sluiten">✕</button></div>
            <div className="fp-q-now">
              <span className="fp-q-art" style={t.artwork ? { backgroundImage: `url("${t.artwork}")` } : undefined} />
              <span className="fp-q-text"><small className="fp-accent-text">NU SPEELT</small><b>{t.title}</b><small>{t.artist}</small></span>
            </div>
            <div className="fp-q-sub"><span>HIERNA</span><span>{Math.max(0, s.queue.length - s.index - 1)} nummers</span></div>
            <ol data-focus-group>
              {s.queue.map((q, i) => i <= s.index ? null : (
                <li key={`${q.id}:${i}`}>
                  <button type="button" className="fp-q" aria-current={i === s.index ? 'true' : undefined} onClick={() => jumpTo(i)}>
                    <span className="fp-q-n">{i - s.index}</span>
                    <span className="fp-q-art" style={q.artwork ? { backgroundImage: `url("${q.artwork}")` } : undefined} />
                    <span className="fp-q-text"><b>{q.title}</b><small>{q.artist}</small></span>
                    <small>{fmtTime(q.duration)}</small>
                  </button>
                </li>
              ))}
            </ol>
          </aside>
        )}
      </div>

      {settings && (
        <div className="fp-settings" role="dialog" aria-label="Speler aanpassen">
          <b>Speler aanpassen</b>
          <div className="fp-set-views">{PLAYER_VIEWS.map(([id, label]) => <button key={id} type="button" className="fp-chip" aria-pressed={view === id} onClick={() => save({ playerView: id })}>{label}</button>)}</div>
          <PSwitch label="Meteen openen bij Afspelen" on={prefs.playerAutoOpen} set={(v) => save({ playerAutoOpen: v })} />
          <PSwitch label="Wazige hoes als achtergrond" on={prefs.playerBlur} set={(v) => save({ playerBlur: v })} />
          <PSwitch label="Songtekst (LRCLIB)" on={prefs.playerLyrics} set={(v) => save({ playerLyrics: v })} />
          <PSwitch label="Kwaliteitslabel" on={prefs.playerQuality} set={(v) => save({ playerQuality: v })} />
          <PSwitch label="Plaat draait mee (Vinyl)" on={prefs.playerSpin} set={(v) => save({ playerSpin: v })} />
          <span className="fp-set-label">Accentkleur</span>
          <div className="fp-swatches">
            {['auto', '#F5A524', '#FFFFFF', '#1ED760', '#FF5C8A', '#7FD1C7'].map((c) => (
              <button key={c} type="button" className={`fp-swatch${c === 'auto' ? ' is-auto' : ''}`} style={c !== 'auto' ? { background: c } : undefined} aria-pressed={prefs.playerAccent === c} onClick={() => save({ playerAccent: c })} aria-label={c === 'auto' ? 'Automatisch uit de hoes' : c} />
            ))}
          </div>
          <button type="button" className="btn btn-sm" onClick={() => setSettings(false)}>Klaar</button>
        </div>
      )}
    </div>
  );
}

function PSwitch({ label, on, set }: { label: string; on: boolean; set: (v: boolean) => void }) {
  return (
    <label className="fp-switch">
      <span>{label}</span>
      <input type="checkbox" checked={on} onChange={(e) => set(e.target.checked)} />
    </label>
  );
}

/** Accent uit de hoeskleur: lichter maken zodat knoppen leesbaar blijven op een donkere achtergrond. */
function lighten(rgb: string): string {
  const m = rgb.match(/\d+/g);
  if (!m) return '#fff';
  const [r, g, b] = m.map(Number);
  const mix = (x: number) => Math.round(x + (255 - x) * 0.45);
  return `rgb(${mix(r)}, ${mix(g)}, ${mix(b)})`;
}
